'use client';

import 'photoswipe/style.css';
import './ProductPageZoom.css';

import { useCarousel } from '@app/_components/ui';
import type { LoopFetchProductResult } from '@server/utils/parseLoop/types';
import type PhotoSwipe from 'photoswipe';
import type PhotoSwipeLightbox from 'photoswipe/lightbox';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { productPageGalleryStore } from './ProductPageGallery.store';

export default function ProductPageZoom({
    children,
    productImages,
}: PropsWithChildren & {
    productImages: LoopFetchProductResult['productImages'];
}) {
    const photoSwipeLightbox = productPageGalleryStore(
        (state) => state.photoSwipe,
    );

    useProductPagePhotoSwipe({
        photoSwipeLightbox,
        productImages,
    });

    return children;
}

function useProductPagePhotoSwipe({
    photoSwipeLightbox,
    productImages,
}: {
    photoSwipeLightbox: PhotoSwipeLightbox | null;
    productImages: LoopFetchProductResult['productImages'];
}) {
    const { scrollTo } = useCarousel();

    return useEffect(() => {
        if (!photoSwipeLightbox) {
            return;
        }

        registerProductPagePhotoSwipeEvents({
            photoSwipeLightbox,
            productImages,
            scrollTo,
        });

        photoSwipeLightbox.init();

        return () => {
            photoSwipeLightbox.destroy();
        };
    }, [photoSwipeLightbox, productImages, scrollTo]);
}

function registerProductPagePhotoSwipeEvents({
    photoSwipeLightbox,
    productImages,
    scrollTo,
}: {
    photoSwipeLightbox: PhotoSwipeLightbox;
    productImages: LoopFetchProductResult['productImages'];
    scrollTo: (index: number) => void;
}) {
    photoSwipeLightbox.on(
        'uiRegister',
        registerCustomThumbnailsElement.bind(undefined, {
            photoSwipeLightbox,
            productImages,
        }),
    );

    photoSwipeLightbox.on('change', () => {
        scrollTo(photoSwipeLightbox.pswp?.currIndex ?? 0);
    });

    photoSwipeLightbox.on('openingAnimationStart', () => {
        photoSwipeLightbox.pswp?.element?.classList.add('pswp--opened');
    });

    photoSwipeLightbox.on('closingAnimationStart', () => {
        photoSwipeLightbox?.pswp?.element?.classList.add('pswp--closing');
    });

    photoSwipeLightbox.on('closingAnimationEnd', () => {
        photoSwipeLightbox?.pswp?.element?.classList.remove('pswp--closing');
        photoSwipeLightbox.pswp?.element?.classList.remove('pswp--opened');
    });
}

function registerCustomThumbnailsElement({
    photoSwipeLightbox,
    productImages,
}: {
    photoSwipeLightbox: PhotoSwipeLightbox;
    productImages: LoopFetchProductResult['productImages'];
}) {
    return photoSwipeLightbox.pswp?.ui?.registerElement({
        name: 'thumbnails',
        className: 'pswp__thumbnails',
        appendTo: 'wrapper',
        onInit: (element, pswp) => {
            const buttons: HTMLButtonElement[] = [];
            let button: HTMLButtonElement;
            let img: HTMLImageElement;
            let prevIndex = -1;

            if (pswp.getNumItems() === 1) {
                return;
            }

            for (let i = 0; i < pswp.getNumItems(); i++) {
                const productImage = productImages[i];

                button = document.createElement('button');
                img = document.createElement('img');

                img.setAttribute('src', productImage.src + '?w=128&h=128&q=80');
                img.setAttribute('alt', productImage.alt);

                button.className = 'pswp__thumbnail';
                button.appendChild(img);

                button.onclick = (event) => {
                    if (event.currentTarget instanceof HTMLButtonElement) {
                        pswp.goTo(buttons.indexOf(event.currentTarget));
                    }
                };

                element.appendChild(button);
                buttons.push(button);
            }

            pswp.on('change', () => {
                scrollToActiveThumbnail({
                    element,
                    pswp,
                    buttons,
                    orientation:
                        window.innerWidth > 1024 ? 'vertical' : 'horizontal',
                });

                updateActiveThumbnail({ pswp, buttons, prevIndex });

                prevIndex = pswp.currIndex;
            });
        },
    });
}

function updateActiveThumbnail({
    pswp,
    buttons,
    prevIndex,
}: {
    pswp: PhotoSwipe;
    buttons: HTMLButtonElement[];
    prevIndex: number;
}) {
    if (prevIndex >= 0) {
        buttons[prevIndex].classList.remove('pswp__thumbnail--active');
    }

    // Add the active class to the current button
    buttons[pswp.currIndex].classList.add('pswp__thumbnail--active');
}

function scrollToActiveThumbnail({
    element,
    pswp,
    buttons,
    orientation,
}: {
    element: HTMLElement;
    pswp: PhotoSwipe;
    buttons: HTMLButtonElement[];
    orientation: 'horizontal' | 'vertical';
}) {
    // Scroll to the first item
    if (pswp.currIndex === 0) {
        element.scrollTo(
            orientation === 'horizontal'
                ? {
                      left: 0,
                      behavior: 'smooth',
                  }
                : {
                      top: 0,
                      behavior: 'smooth',
                  },
        );
        return;
    }

    // Scroll to the last item
    if (pswp.currIndex === pswp.getNumItems() - 1) {
        element.scrollTo(
            orientation === 'horizontal'
                ? { left: element.scrollWidth, behavior: 'smooth' }
                : { top: element.scrollHeight, behavior: 'smooth' },
        );
        return;
    }

    const horizontalCenterPosition =
        buttons[pswp.currIndex].offsetLeft -
        (pswp.getViewportCenterPoint().x -
            buttons[pswp.currIndex].getBoundingClientRect().width / 2);
    const verticalCenterPosition =
        buttons[pswp.currIndex].offsetTop -
        (pswp.getViewportCenterPoint().y -
            buttons[pswp.currIndex].getBoundingClientRect().height / 2);

    // Scroll to the center of the active thumbnail
    element.scrollTo(
        orientation === 'horizontal'
            ? {
                  left: horizontalCenterPosition,
                  behavior: 'smooth',
              }
            : {
                  top: verticalCenterPosition,
                  behavior: 'smooth',
              },
    );
}
